import React from "react";
import { LogIn, UserPlus, Sparkles, X } from "lucide-react";

/**
 * Premium blocking modal that fires when a guest has exhausted their
 * single guest credit, or attempts a paywalled action (e.g. opening
 * Operator Logs). Tapping the X simply closes the modal — the actual
 * gate logic lives in `ScanInterface`, so the underlying workspace
 * remains frozen until the user authenticates.
 */
const GuestExhaustedModal = ({ open, onClose, onAuth, reason }) => {
  if (!open) return null;
  return (
    <div
      data-testid="guest-exhausted-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-3 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-exhausted-title"
    >
      <button
        type="button"
        data-testid="guest-modal-backdrop"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md bg-[#020617] border border-[#FF0033]/60 shadow-[0_24px_80px_rgba(0,0,0,0.7)] p-6 sm:p-8 chaos-reveal">
        <button
          type="button"
          data-testid="guest-modal-dismiss"
          onClick={onClose}
          aria-label="Dismiss modal"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#64748b] hover:text-[#FF0033] transition-colors"
        >
          <X size={14} />
        </button>

        <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#FF0033]/50 bg-[#FF0033]/[0.08] mb-5">
          <Sparkles size={11} className="text-[#FF0033]" />
          <span className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-[#FF0033]">
            {reason === "history" ? "Sign-in required" : "Guest credit exhausted"}
          </span>
        </div>

        <h2
          id="guest-exhausted-title"
          data-testid="guest-modal-title"
          className="font-mono text-[20px] sm:text-2xl uppercase tracking-[0.04em] leading-[1.15] text-[#f1f5f9] mb-3"
        >
          {reason === "history"
            ? "Your scan history lives behind sign-in."
            : "You've used your guest credit."}
        </h2>
        <p className="text-[#cbd5e1] text-sm sm:text-[15px] leading-relaxed mb-7">
          Create an account or log in now to claim your{" "}
          <span className="text-[#FF0033] font-bold">
            5 free welcome credits
          </span>{" "}
          and save your scan history log.
        </p>

        <div className="flex flex-col sm:flex-row gap-2.5">
          <button
            type="button"
            data-testid="guest-modal-signin"
            onClick={onAuth}
            className="inline-flex flex-1 items-center justify-center gap-2 bg-[#FF0033] hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.18em] px-5 py-3 text-[11px] border border-transparent hover:border-white transition-colors"
          >
            <LogIn size={13} />
            Sign In
          </button>
          <button
            type="button"
            data-testid="guest-modal-signup"
            onClick={onAuth}
            className="inline-flex flex-1 items-center justify-center gap-2 border border-white/15 hover:border-[#FF0033] hover:text-[#FF0033] text-[#cbd5e1] font-mono uppercase tracking-[0.18em] px-5 py-3 text-[11px] transition-colors"
          >
            <UserPlus size={13} />
            Create Account
          </button>
        </div>
        <p className="mt-5 font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#475569] text-center">
          +5 credits · scan history · cross-device sync
        </p>
      </div>
    </div>
  );
};

export default GuestExhaustedModal;
